'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

export default function LoginForm() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { remember: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setAuthError('');

    try {
      let emailToUse = data.email.trim();

      // If the input doesn't look like an email, look up the username in app_users
      if (!emailToUse.includes('@')) {
        const supabase = createClient();
        const { data: appUser, error: lookupError } = await supabase
          .from('app_users')
          .select('email')
          .ilike('username', emailToUse)
          .maybeSingle();

        if (!lookupError && appUser?.email) {
          emailToUse = appUser.email;
        } else {
          // Fallback: try the @emicor.com convention
          emailToUse = `${emailToUse}@emicor.com`;
        }
      }

      await signIn(emailToUse, data.password);
      router.push('/');
      router.refresh();
    } catch (err: any) {
      setIsLoading(false);
      setAuthError(
        'Usuario o contraseña incorrectos. Verifica tus credenciales e intenta nuevamente.'
      );
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center p-8 lg:p-12 bg-card">
      <div className="w-full max-w-md">
        {/* Mobile logo */}
        <div className="flex items-center gap-2 mb-8 lg:hidden">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
            <ShieldCheck size={18} className="text-white" />
          </div>
          <span className="font-bold text-foreground text-xl">EMICOR</span>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={16} className="text-primary" />
            <span className="text-xs font-semibold text-primary uppercase tracking-widest">
              Acceso Seguro
            </span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            Iniciar Sesión
          </h1>
          <p className="text-muted-foreground text-sm mt-1.5">
            Ingresa tus credenciales para acceder al sistema EMICOR.
          </p>
        </div>

        {/* Auth Error */}
        {authError && (
          <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6 animate-slide-up">
            <AlertCircle size={16} className="text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{authError}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Email */}
          <div>
            <label htmlFor="email" className="label-text">
              Usuario
            </label>
            <input
              id="email"
              type="text"
              autoComplete="username"
              placeholder="Nombre de usuario"
              className={`input-field ${errors.email ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
              {...register('email', {
                required: 'El usuario es obligatorio.',
                validate: (value) => {
                  const trimmed = value.trim();
                  if (!trimmed) return 'El usuario es obligatorio.';
                  return true;
                },
              })}
            />
            {errors.email && (
              <p className="error-text">{errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="password" className="label-text mb-0">
                Contraseña
              </label>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Ingresa tu contraseña"
                className={`input-field pr-10 ${errors.password ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
                {...register('password', {
                  required: 'La contraseña es obligatoria.',
                  minLength: {
                    value: 4,
                    message: 'La contraseña debe tener al menos 4 caracteres.',
                  },
                })}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={
                  showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                }
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && (
              <p className="error-text">{errors.password.message}</p>
            )}
          </div>

          {/* Remember me */}
          <div className="flex items-center gap-2.5">
            <input
              id="remember"
              type="checkbox"
              className="w-4 h-4 rounded border-input accent-primary cursor-pointer"
              {...register('remember')}
            />
            <label
              htmlFor="remember"
              className="text-sm text-muted-foreground cursor-pointer select-none"
            >
              Recordarme en este dispositivo
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full py-3 text-base"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Verificando credenciales...
              </>
            ) : (
              'Ingresar al Sistema'
            )}
          </button>
        </form>

        {/* Role info */}
        <div className="mt-8 p-4 bg-secondary/60 rounded-xl border border-border">
          <div className="flex items-start gap-2.5">
            <ShieldCheck size={15} className="text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-foreground mb-1">
                Sistema de acceso restringido
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Este sistema es exclusivo para el equipo interno de EMICOR. El
                acceso no autorizado está prohibido y puede ser monitoreado.
              </p>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          © 2026 EMICOR. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
}